import { NO_PROXY } from '@hit-game/shared/constants';
import type { IAccountCredentials, IAccountDataResponse } from '@hit-game/shared/types';
import { Check, Copy, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { accountService } from '../../services/account.service';
import { formatMoneyOrUnknown } from '../../utils/format';
import { Modal } from '../common/Modal';

interface InfoRowProps {
  label: string;
  value: string;
  /** Giá trị đem đi chép — khác `value` khi ô đang che (mật khẩu). */
  copyValue?: string;
  masked?: boolean;
  onToggleMask?: () => void;
}

function InfoRow({ label, value, copyValue, masked, onToggleMask }: InfoRowProps) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard.writeText(copyValue ?? value).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 1500);
    });
  };

  return (
    <div className="border-line bg-canvas flex items-center gap-2 rounded-lg border px-3 py-2">
      <span className="text-ink-dim w-28 shrink-0 text-[10px] font-bold uppercase tracking-wider">
        {label}
      </span>
      <span className="text-ink min-w-0 flex-1 truncate font-mono text-[12px]" title={value}>
        {value || '—'}
      </span>

      {onToggleMask && (
        <button
          type="button"
          onClick={onToggleMask}
          title={masked ? 'Hiện' : 'Ẩn'}
          className="text-ink-dim hover:text-ink shrink-0 rounded p-1 transition-colors"
        >
          {masked ? <Eye size={13} /> : <EyeOff size={13} />}
        </button>
      )}

      <button
        type="button"
        onClick={copy}
        disabled={!value}
        title={copied ? 'Đã chép' : 'Chép'}
        className="text-ink-dim hover:text-accent shrink-0 rounded p-1 transition-colors disabled:opacity-30"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
}

interface AccountInfoModalProps {
  /** `null` = đóng. Mở tài khoản khác thì nạp lại từ đầu. */
  account: IAccountDataResponse | null;
  onClose: () => void;
}

/**
 * Thông tin một tài khoản: username, tên hiển thị, mật khẩu, proxy, IP, số dư.
 *
 * Mật khẩu đọc từ endpoint riêng ngay lúc mở popup, không đi kèm dữ liệu sảnh —
 * sảnh tự tải lại 30 giây một lần cho mọi tài khoản.
 */
export function AccountInfoModal({ account, onClose }: AccountInfoModalProps) {
  const [info, setInfo] = useState<IAccountCredentials | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [masked, setMasked] = useState(true);

  const accountId = account ? String(account._id) : null;

  useEffect(() => {
    if (!accountId) return;

    setInfo(null);
    setFailed(false);
    setMasked(true);
    setLoading(true);

    const load = async () => {
      try {
        setInfo(await accountService.credentials(accountId));
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [accountId]);

  const proxy = info?.proxy ?? NO_PROXY;

  return (
    <Modal
      open={account !== null}
      onClose={onClose}
      maxWidth={480}
      title={
        <span className="flex items-center gap-2">
          Thông tin tài khoản
          <span className="text-ink-dim font-mono text-[12px] font-normal">
            {account?.username}
          </span>
        </span>
      }
    >
      {loading && (
        <div className="text-ink-dim flex items-center justify-center gap-2 py-10 text-[13px]">
          <Loader2 size={16} className="animate-spin" />
          Đang tải thông tin...
        </div>
      )}

      {!loading && (failed || !info) && (
        <div className="text-danger py-10 text-center text-[13px]">
          Không lấy được thông tin tài khoản.
        </div>
      )}

      {!loading && info && (
        <div className="space-y-2">
          <InfoRow label="Username" value={info.username} />
          <InfoRow label="Tên hiển thị" value={info.fullname} />
          <InfoRow
            label="Mật khẩu"
            value={masked ? '•'.repeat(info.password.length) : info.password}
            copyValue={info.password}
            masked={masked}
            onToggleMask={() => {
              setMasked((v) => !v);
            }}
          />
          <InfoRow label="Proxy" value={proxy} />
          {/* IP là phần đầu của chuỗi proxy — tách sẵn để khỏi phải tự cắt tay. */}
          <InfoRow label="IP" value={proxy === NO_PROXY ? '' : (proxy.split(':')[0] ?? '')} />
          <InfoRow label="Số dư" value={formatMoneyOrUnknown(account?.balance)} />
        </div>
      )}
    </Modal>
  );
}
